



































import API from '@core/services/API'
import { IDateService } from '@master/services/IDateService'
import { inject } from 'inversify-props'
import { Component, Vue, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import AppDatatable from '@core/components/AppDatatable.vue'
import AppDialog from '@core/components/AppDialog.vue'
import AppForm from '@core/components/AppForm.vue'
import { FormStructure } from '@master/model/structure/FormStructure'
import { ClientPolicyDto } from '../../api'
import { PolicyClientMeta } from '../../model/PolicyClientMeta'
import { PolicyDocumentMeta } from '../../model/PolicyDocumentMeta'
import { IProjectInfo } from '@master/config/IProjectInfo'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'

const clientPolicyStore = namespace('ClientPolicyStore')

@Component({
  components: {
    AppDatatable,
    AppDialog,
    AppForm
  }
})
export default class SubmittedDocuments extends Vue {
  @inject()
  private $api: API

  @inject()
  private dateService: IDateService

  @inject()
  private projectInfo: IProjectInfo

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  @clientPolicyStore.State
  policy: ClientPolicyDto

  private newPolicyDocument = new PolicyDocumentMeta()
  private policyDocuments: PolicyDocumentMeta[] = []
  private selectedPolicyDocuments: PolicyDocumentMeta[] = []
  private disableDeleteDocumentsButton = true
  private newDocumentModal = false
  private submittedDocument: FormData = new FormData()

  get policyOid () {
    return this.$route.query.policy_oid
  }

  get isDisabled () {
    return this.$store.getters.currentUser.systemrole !== 'ops-staff'
  }

  get personalFinancialReview () {
    return this.projectInfo.personalFinancialReview ?? 'Personal Financial Review'
  }

  get newDocumentFormStructure (): FormStructure<PolicyDocumentMeta> {
    return [
      {
        label: 'Document Type',
        options: [
          { value: 'NRIC', label: '1. NRIC (New Business)' },
          { value: 'Application Forms', label: '2. Application Forms (New Business)' },
          { value: 'Policy Illustration', label: '3. Policy Illustration (New Business)' },
          { value: 'Personal Financial Review', label: '4. Personal Financial Review (New Business)' },
          { value: 'Birth Certificate', label: '5. Birth Certificate (New Business)' },
          { value: 'Medical Reports', label: '6. Medical Reports (New Business)' },
          { value: 'Cheque Image', label: '7. Cheque Image (New Business)' },
          { value: 'Terms and Conditions', label: '8. Terms & Conditions (New Business)' },
          { value: 'Others', label: '9. Others (New Business)' },
          { value: 'ECDD Form', label: '10. ECDD Form (New Business)' },
          { value: 'Replacement Form', label: '11. Replacement Form (New Business)' },
          { value: `${this.personalFinancialReview} Supp Form`, label: `12. ${this.personalFinancialReview} Supp Form (New Business)` },
          { value: 'Service Recovery', label: '1. Service Recovery (Follow Up)' },
          { value: 'Claims', label: '2. Claims (Follow Up)' },
          { value: 'Change of FAR', label: '3. Change of FAR (Follow Up)' },
          { value: 'Nomination Form', label: '4. Nomination Form (Follow Up)' }
        ],
        key: 'documentType',
        classes: ['colspan']
      }
    ]
  }

  get policyDocumentsColumns (): Array<{ label: string; field: string }> {
    return [
      { label: 'File Name', field: 'fileName' },
      { label: 'Document Type', field: 'documentType' },
      { label: 'Date Uploaded', field: 'dateUploaded' }
    ]
  }

  get policyDocumentsData () {
    return this.policyDocuments.map(e => {
      const dateUploaded = this.dateService.format(e.ctm * 1000, 'YYYY-MM-DD')
      return {
        ...e,
        dateUploaded,
        emit: true
      }
    })
  }

  openNewDocumentModal () {
    this.newDocumentModal = true
    this.newPolicyDocument = new PolicyDocumentMeta()
  }

  handleDocumentSelection (rows: PolicyDocumentMeta[]) {
    this.selectedPolicyDocuments = rows
  }

  handleDocumentClick (row: PolicyDocumentMeta) {
    this.downloadDocument(row.oid, row.fileName)
  }

  @Watch('selectedPolicyDocuments')
  selectedPolicyDocumentsOnChange (val: PolicyDocumentMeta[]) {
    this.disableDeleteDocumentsButton = val.length === 0
  }

  async fetchPolicyDocuments () {
    const headers = [
      '_oid',
      'policy_oid',
      'file_name',
      'document_type',
      'ctm'
    ]

    const res: { data: Array<any[]> } = await this.$api.clientPolicy.policyDocumentsList({
      headers: JSON.stringify(headers),
      query: 'policy_oid = ?',
      queryArgs: JSON.stringify([this.policy.oid])
    })

    this.policyDocuments = res.data.map(doc => {
      const policyDoc = new PolicyDocumentMeta()
      policyDoc.assignData(headers, doc)
      return policyDoc
    })
  }

  downloadDocument (documentOid, fileName) {
    const link = `/live/getPolicyDocument?fileType=submitted&documentOid=${documentOid}&fileName=${fileName}`
    window.open(link, '_blank')
  }

  documentUploadedChange (files) {
    const file = files[0]
    const data = new FormData()
    data.append('policy_profile_oid', this.policy.oid)
    data.append('uploaded_policy_document', file)
    data.append('file_name', file.name)
    data.append('fileSizeInString', file.size.toString())
    this.submittedDocument = data
  }

  async createNewDocument () {
    // check if file size exist 20MB, not allowed to upload
    const fileSizeInString = this.submittedDocument.get('fileSizeInString')
    const fileSizeInMB = Number(fileSizeInString)

    if (fileSizeInMB === 0) {
      await this.uiService.alert.show({
        type: 'warning',
        message: 'No file is selected'
      })
      return
    }
    if (fileSizeInMB > 20000000) {
      await this.uiService.alert.show({
        type: 'warning',
        message: 'Size of document cannot more than 20MB'
      })
      return
    }

    this.submittedDocument.append('document_type', this.newPolicyDocument.documentType)
    await this.$api.clientPolicy.uploadNewPolicyDocument(this.submittedDocument)
    await this.fetchPolicyDocuments()
    this.newDocumentModal = false
  }

  async deletePolicyDocuments () {
    await Promise.all(this.selectedPolicyDocuments.map(async (e) => {
      await this.$api.clientPolicy.deletePolicyDocument({
        /* eslint-disable @typescript-eslint/camelcase */
        document_oid: e.oid
      })
    }))
    await this.fetchPolicyDocuments()
  }

  async mounted () {
    await this.fetchPolicyDocuments()
    this.uiService = this.sharedVariable.uiService
  }
}
